import {EntityState, EntityStore, QueryEntity, selectPersistStateInit, StoreConfig} from '@datorama/akita';
import {Injectable} from '@angular/core';
import {ClassificatorValueQuery} from './classificator-value.query';
import {filter, map, shareReplay, tap} from 'rxjs/operators';
import {CONSTANTS} from './classificator-value.constants';
import {plainToInstance} from 'class-transformer';
import {Observable, of, Subject} from 'rxjs';
import {IExpiringStore} from "../../IExpiringStore";
import {MesHttpStatusHandlerService} from "../../mes-http-status-handler.service";
import {getIdsInArrayWrapper} from '../../helpers/entity-store-helpers';
import {removeRedundantKeys} from "../../helpers/general-helpers";
import {StoreHttpService} from "../../store-http.service";

interface State extends EntityState<ClassificatorValueQuery> {
}

@Injectable({
  providedIn: 'root'
})
@StoreConfig({name: 'ClassificatorValue', idKey: 'Id'})
export class ClassificatorValuesStore extends EntityStore<State, ClassificatorValueQuery> implements IExpiringStore {
  expire$: Subject<string | string[] | null> = new Subject();

  constructor(private mesHttpStatusHandlerService: MesHttpStatusHandlerService, private storeHttpService: StoreHttpService) {
    super();

    mesHttpStatusHandlerService.cacheExpire$
      .pipe(filter(value => value.region?.toLocaleLowerCase() === this.storeName.toLocaleLowerCase()))
      .subscribe(value => {
        if (value.id && value.entities) {
          this.remove(value.id);
          let entities = removeRedundantKeys(value.entities, [...ClassificatorValueQuery.Columns()]);
          this.add(plainToInstance(ClassificatorValueQuery, entities));
        } else {
          this.expire$.next(value.id);
        }
      });

    mesHttpStatusHandlerService.cacheRemove$
      .pipe(filter(value => value.region?.toLocaleLowerCase() === this.storeName.toLocaleLowerCase()))
      .subscribe(value => this.remove(value.id));

    selectPersistStateInit().subscribe(v => {
      if (!this.getValue().ids.length) {
        this.loadAll().subscribe();
      }
    });
    this.expire$.subscribe(value => {
      this.loadAll(value).subscribe();
    });
  }

  loadAll(ids: string | string[] | null = null) {
    this.setHasCache(false);
    this.setLoading(true);
    const idsFilter = getIdsInArrayWrapper(ids);
    const request$ = this.storeHttpService.addGet<{items: string[]}, ClassificatorValueQuery[]>(idsFilter, {
      action: 'GetByIds',
      controller: this.storeName
    }).pipe(map(value => plainToInstance(ClassificatorValueQuery, value.data)),
      tap(clvs => {
          if (idsFilter.items.length) {
            this.remove(clvs.map(clv => clv.Id));
            this.add(clvs);
          } else {
            this.set(clvs);
          }
          this.setHasCache(true);
          this.setLoading(false);
        }
      ));
    return this.cache.active.getValue() ? of() : request$;
  }
}

@Injectable({
  providedIn: 'root'
})
export class ClassificatorValuesQuery extends QueryEntity<State, ClassificatorValueQuery> {
  constructor(protected store: ClassificatorValuesStore) {
    super(store);
  }
  loadAll: (ids?: (string | string[] | null)) => Observable<unknown> = (ids: string | string[] | null = null) => this.store.loadAll(ids);
  getAll$: Observable<ClassificatorValueQuery[]> = this.selectAll();

  propertyTypes$ = this.getByClassificatorId(CONSTANTS.PropertyType.ClassificatorId);
  mandatories$ = this.getByClassificatorId(CONSTANTS.Mandatory.ClassificatorId);
  propertyGroups$ = this.getByClassificatorId(CONSTANTS.PropertyGroup.ClassificatorId);
  physicalDimensions$ = this.getByClassificatorId(CONSTANTS.PhysicalDimension.ClassificatorId);
  timeConstraintMethods$ = this.getByClassificatorId(CONSTANTS.TimeConstraintMethod.ClassificatorId);
  itemTypes$ = this.getByClassificatorId(CONSTANTS.ItemType.ClassificatorId);
  itemStatuses$ = this.getByClassificatorId(CONSTANTS.ItemStatus.ClassificatorId);
  obtainMethods$ = this.getByClassificatorId(CONSTANTS.ObtainMethod.ClassificatorId);
  autoReplenishFlags$ = this.getByClassificatorId(CONSTANTS.AutoReplenishFlag.ClassificatorId);
  resourceTypes$ = this.getByClassificatorId(CONSTANTS.ResourceType.ClassificatorId);
  resourceKinds$ = this.getByClassificatorId(CONSTANTS.ResourceKind.ClassificatorId);
  bottlenecks$ = this.getByClassificatorId(CONSTANTS.Bottleneck.ClassificatorId);
  operationStatuses$ = this.getByClassificatorId(CONSTANTS.OperationStatus.ClassificatorId);
  operationTypes$ = this.getByClassificatorId(CONSTANTS.OperationType.ClassificatorId);
  operationPlacements$ = this.getByClassificatorId(CONSTANTS.Placement.ClassificatorId);
  orderStatuses$ = this.getByClassificatorId(CONSTANTS.OrderStatus.ClassificatorId);
  orderTypes$ = this.getByClassificatorId(CONSTANTS.ProductionOrderOrderType.ClassificatorId);
  orderClasses$ = this.getByClassificatorId(CONSTANTS.ProductionOrderOrderClass.ClassificatorId);
  resourceConnectionTypes$ = this.getByClassificatorId(CONSTANTS.ResourceConnectionType.ClassificatorId);
  assignedResourceQuantityFlagClv$ = this.getByClassificatorId(CONSTANTS.AssignedResourceQuantityFlag.ClassificatorId);
  resourceQuantityConstraintsClv$ = this.getByClassificatorId(CONSTANTS.ResourceQuantityConstraints.ClassificatorId);
  itemGroupTypes$ = this.getByClassificatorId(CONSTANTS.ItemGroupType.ClassificatorId);
  costPriceComponentTypes$ = this.getByClassificatorId(CONSTANTS.CostPriceComponentType.ClassificatorId);
  costPriceComponentSurchargeTypes$ = this.getByClassificatorId(CONSTANTS.CostPriceComponentSurchargeType.ClassificatorId);
  mappingOrigins$ = this.getByClassificatorId(CONSTANTS.MappingOrigin.ClassificatorId);
  configurationStatuses$ = this.getByClassificatorId(CONSTANTS.ConfiguratorStatus.ClassificatorId);
  specPriorities$ = this.getByClassificatorId(CONSTANTS.SpecPriority.ClassificatorId);
  configureOrderHeaderStatuses$ = this.getByClassificatorId(CONSTANTS.ConfigureOrderHeaderStatus.ClassificatorId);
  configureOrderLineStatuses$ = this.getByClassificatorId(CONSTANTS.ConfigureOrderLineStatus.ClassificatorId);
  itemGroupStatuses$ = this.getByClassificatorId(CONSTANTS.ItemGroupStatus.ClassificatorId);
  itemOwners$ = this.getByClassificatorId(CONSTANTS.Owner.ClassificatorId);
  itemInItemGroupTypes$ = this.getByClassificatorId(CONSTANTS.ItemInItemGroupType.ClassificatorId);
  orderHeaderStatuses$ = this.getByClassificatorId('8b1d1c6e-327a-4360-92aa-183cb471c039');

  private getByClassificatorId(classificatorId: string) {
    return this.getAll$.pipe(map(values => values.filter(value => value.ClassificatorId === classificatorId)), shareReplay(1));
  }

  remove(id: string) {
    this.store.remove(id);
  }
}
